import { useEffect, useState } from "react";
import { IncidentForm } from "./IncidentFormDialog.default";
import { Form } from "antd";
import {
  addIncident,
  deleteIncident,
  getCompanies,
  getCompanyDepartments,
  getIncidentTypes,
  getPlatformUsers,
} from "../../repository/data.repository";
import { getProblemMessage } from "../../utils/http";
import {
  IncidentSeverityType,
  IncidentStatusType,
} from "../../repository/data.default";
import DynamicForm from "../../controls/DynamicForm";
import {
  createSearchRequest,
  createScopedSearchRequest,
} from "../../hooks/useSearchRequest";
import InModal from "../../controls/InModal";
import { modalTitle } from "../../controls/InModal.default";
import ModalFooter from "../../controls/ModalFooter";
import ModalDeleteConfirmation from "../../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../../controls/ModalErrorAlert";

export interface IncidentFormDialogProps {
  readonly open?: boolean;
  // A reported incident is then worked through the Manage dialog — this dialog only creates or deletes.
  readonly mode: "add" | "delete";
  readonly initialValues?: IncidentForm;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

/**
 * Lean create surface for a new incident (and the delete confirmation). Reporting is a short form;
 * once an incident exists, its status, assignee and comment thread are handled in IncidentManageDialog.
 */
function IncidentFormDialog(props: IncidentFormDialogProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch<IncidentForm>([], form);

  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (props.open) {
      form.resetFields();
      setErrorMessage("");
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then((value) => setValid(!!Object.values(value).length))
      .catch(() => setValid(false));
  }, [form, values, props.open]);

  // Events

  function onSubmit(): void {
    if (props.mode === "delete") {
      if (!props.initialValues?.uid) return;
      run(() => deleteIncident(props.initialValues!.uid!));
      return;
    }

    const { company, department, assignedToUser, incidentType, description, severity, title, isAnonymous } =
      values;
    run(() =>
      addIncident({
        companyUid: company?.value,
        departmentUid: department?.value,
        assignedToUserUid: assignedToUser?.value,
        incidentTypeUid: incidentType?.value,
        description,
        severity: severity?.value,
        // New incidents always start Open; the lifecycle is driven from the Manage dialog.
        status: IncidentStatusType.Open,
        title,
        isAnonymous: !!isAnonymous,
      }),
    );
  }

  function run(action: () => Promise<unknown>): void {
    setLoading(true);
    setErrorMessage("");
    action()
      .then(() => {
        setLoading(false);
        props.onFinish?.();
      })
      .catch((err) => {
        setLoading(false);
        setErrorMessage(getProblemMessage(err));
      });
  }

  // Requests

  const companiesRequest = createSearchRequest(getCompanies, "name");

  // Department + assignee follow the company picked above (cleared via dependsOn when it changes).
  const departmentsRequest = createScopedSearchRequest(
    getCompanyDepartments,
    "name",
    "companyUid",
    () => form.getFieldValue("company")?.value,
  );

  const usersRequest = createScopedSearchRequest(
    getPlatformUsers,
    "userName",
    "companyUid",
    () => form.getFieldValue("company")?.value,
  );

  // Incident types are a platform-level (unscoped) reference catalog.
  const incidentTypesRequest = createSearchRequest(getIncidentTypes, "name");

  const severityRequest = () =>
    Promise.resolve(Object.values(IncidentSeverityType).map((s) => ({ display: s, value: s })));

  return (
    <InModal
      open={props.open}
      size={props.mode === "delete" ? "small" : "large"}
      title={modalTitle(props.mode, "Incident", props.initialValues?.title)}
      onCancel={props.onCancel}
      actions={
        <ModalFooter
          mode={props.mode}
          loading={loading}
          valid={valid}
          onCancel={props.onCancel}
          onSubmit={onSubmit}
          submitText={props.mode === "add" ? "Report" : undefined}
        />
      }
    >
      {props.mode === "delete" ? (
        <ModalDeleteConfirmation
          entityLabel="incident"
          entityName={props.initialValues?.title}
          additionalText="This also removes its comments and cannot be undone."
        />
      ) : (
        <DynamicForm
          form={form}
          schema={{
            groups: [
              {
                controls: [
                  { type: "combobox", name: "company", label: "Company", request: companiesRequest, rules: [{ required: true, message: "Company is required" }] },
                  { type: "combobox", name: "department", label: "Department", request: departmentsRequest, dependsOn: ["company"] },
                  { type: "combobox", name: "incidentType", label: "Type", request: incidentTypesRequest, rules: [{ required: true, message: "Type is required" }] },
                  { type: "textbox", name: "title", label: "Title", rules: [{ required: true, message: "Title is required" }] },
                  { type: "combobox", name: "severity", label: "Severity", request: severityRequest, rules: [{ required: true, message: "Severity is required" }] },
                  { type: "combobox", name: "assignedToUser", label: "Assignee", request: usersRequest, dependsOn: ["company"] },
                  { type: "areabox", name: "description", label: "Description", rows: 5, rules: [{ required: true, message: "Description is required" }] },
                  { type: "togglebox", name: "isAnonymous", label: "Report anonymously", checkedText: "Yes", unCheckedText: "No" },
                ],
              },
            ],
          }}
        />
      )}

      <ModalErrorAlert message={errorMessage} />
    </InModal>
  );
}

export default IncidentFormDialog;
